// Phase 2.11 Tier 4 — per-asset deliverable upload controller.
//
// Per-deliverable-type accordion. Each "Upload" / "Replace" button
// remembers which deliverable type it triggered via dataset, then
// clicks the hidden file picker; the chosen file rides the standard
// direct-S3 chip pipeline:
//   1) POST presignUrl → { uploads: [{ upload_url, cache_name }] }
//   2) PUT each file to S3 (signed URL, no auth header)
//   3) POST createUrl with deliverable_type + cache_name + filename
//   4) Reload the page so the panel re-renders with the fresh row
//      and the readiness gauge updates.
//
// `_lastDeliverableType` is set on the controller before clicking the
// file picker so `filesSelected` knows which accordion the upload
// belongs to. (Hidden <input type=file> can't carry per-button state
// natively without rebuilding the input each time.)

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "uploadList", "card"]

  static values = {
    presignUrl: String,
    createUrl: String,
    csrf: String
  }

  openFilePicker(event) {
    const button = event.currentTarget
    this._lastDeliverableType = button.dataset.deliverableType
    if (!this._lastDeliverableType) return
    this.fileInputTarget?.click()
  }

  async filesSelected(event) {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    const type = this._lastDeliverableType
    if (!type) return

    for (const file of files) {
      await this._uploadFile(file, type)
    }
    event.target.value = ""
  }

  async _uploadFile(file, deliverableType) {
    const chip = this._createChip(`${deliverableType}: ${file.name}`, "uploading")
    this.uploadListTarget?.appendChild(chip)

    const csrf = this.csrfValue ||
                 document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: [{
            filename: file.name,
            content_type: file.type || "application/octet-stream",
            byte_size: file.size
          }]
        })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to presign upload")
      }
      const { uploads } = await presignRes.json()
      const upload = uploads[0]
      if (!upload) throw new Error("Empty presign response")

      const putRes = await fetch(upload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || upload.content_type },
        body: file
      })
      if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`)

      const createRes = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
        body: JSON.stringify({
          deliverable_type: deliverableType,
          cache_name: upload.cache_name,
          filename: file.name
        })
      })
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to register deliverable")
      }
      const result = await createRes.json()
      this._setChipDone(chip, result.filename || file.name)

      // Reload so readiness gauge + accordion state both refresh.
      setTimeout(() => window.location.reload(), 600)
    } catch (e) {
      console.error("[asset-deliverables] upload failed:", e)
      this._setChipFailed(chip)
    }
  }

  _createChip(name, state) {
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs"
    chip.classList.add(state === "uploading" ? "chip-uploading" : "chip-file")
    const truncated = name.length > 36 ? name.slice(0, 32) + "..." + name.slice(name.lastIndexOf(".")) : name
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span class="truncate max-w-[200px]" title="${name}">${truncated}</span>
    `
    return chip
  }

  _setChipDone(chip, name) {
    chip.classList.remove("chip-uploading")
    chip.classList.add("chip-file")
    const truncated = name.length > 32 ? name.slice(0, 28) + "..." + name.slice(name.lastIndexOf(".")) : name
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
      </svg>
      <span class="truncate max-w-[200px]" title="${name}">${truncated}</span>
    `
  }

  _setChipFailed(chip) {
    chip.classList.remove("chip-uploading")
    chip.classList.add("bg-rose-50", "text-rose-700")
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
      </svg>
      <span>Upload failed</span>
    `
  }
}
